/**
 * @fileoverview Prompt de instalação PWA e notificação de atualização.
 *
 * Exibe banner quando o navegador dispara `beforeinstallprompt` (Chrome/Edge/
 * Android). Para iOS Safari — que não suporta o evento — exibe um banner
 * específico com instruções "Compartilhar → Adicionar à Tela de Início".
 * Também mostra toast quando o Service Worker detecta nova versão.
 * O banner pode ser dispensado (estado salvo no localStorage).
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Smartphone, Download, X, RefreshCw, Share, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Cooldown after the user clicks "Agora não" — don't show the banner again
// for this many days. The previous logic used a boolean flag that was never
// reset, so the prompt could only be installed (never re-prompted) after a
// single dismiss.
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
// Small delay before the banner appears so it doesn't pop in mid-page-load
// while the user is still orienting themselves.
const BANNER_DELAY_MS = 4000;

function isDismissedRecently(): boolean {
  // Backwards-compat: legacy boolean string is treated as "dismissed forever"
  // until the user clears localStorage or the cooldown timestamp is set.
  const legacy = localStorage.getItem('pwa-banner-dismissed');
  if (legacy === 'true') return true;
  const ts = Number(localStorage.getItem('pwa-banner-dismissed-at') || '0');
  if (!ts) return false;
  return Date.now() - ts < DISMISS_COOLDOWN_MS;
}

/**
 * iOS Safari (real Safari, not the wrapped WebViews) doesn't fire
 * `beforeinstallprompt`. To still nudge iPhone/iPad users to install we sniff
 * the UA + match the Safari `standalone` API.
 */
function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  // iPad on iOS 13+ identifies as Mac — fall back to touch points.
  const isIpadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  if (!isIos && !isIpadOS) return false;
  // Chrome / Firefox / Edge on iOS use WebKit but their UA includes CriOS /
  // FxiOS / EdgiOS — Add-to-Home-Screen only works in real Safari.
  return /Safari/.test(ua) && !/(CriOS|FxiOS|EdgiOS|OPiOS)/.test(ua);
}

/**
 * The PWA is already installed when the page is loaded in standalone display
 * mode (or via the iOS Safari `standalone` flag). No point nagging the user
 * to install something they already have.
 */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // Older iOS Safari exposes a non-standard `standalone` boolean on navigator.
  return !!(navigator as any).standalone;
}

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  // Listen for install prompt (Chrome/Edge/Android). iOS Safari doesn't fire
  // this — handled separately below.
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      if (!isDismissedRecently()) {
        setTimeout(() => setShowBanner(true), BANNER_DELAY_MS);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // iOS Safari fallback — the OS doesn't expose a programmatic install API,
  // so we just show the user *how* to install via the share sheet.
  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (isDismissedRecently()) return;
    if (!isIosSafari()) return;
    const t = setTimeout(() => {
      setIosMode(true);
      setShowBanner(true);
    }, BANNER_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  // Listen for SW update (vite-plugin-pwa registers this)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready.then(registration => {
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            toast.info('Nova versão disponível!', {
              description: 'Recarregue para aplicar a atualização.',
              duration: 10000,
              action: {
                label: 'Atualizar',
                onClick: () => window.location.reload(),
              },
              icon: <RefreshCw className="h-4 w-4" />,
            });
          }
        });
      });
    });
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      toast.success('App instalado com sucesso!');
    }
    setDeferredPrompt(null);
    setShowBanner(false);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShowBanner(false);
    // Store timestamp instead of boolean; isDismissedRecently re-checks against
    // the cooldown window so the prompt eventually returns.
    localStorage.setItem('pwa-banner-dismissed-at', String(Date.now()));
    localStorage.removeItem('pwa-banner-dismissed'); // clean legacy flag
  }, []);

  // Show only when there's something to do: either a real install event
  // (Chrome/Edge/Android) OR we're running on iOS Safari with the manual
  // instructions variant.
  if (!showBanner || (!deferredPrompt && !iosMode)) return null;

  return (
    <div
      className={cn(
        'fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-50',
        'bg-card border border-border rounded-2xl shadow-2xl p-4',
        'animate-in slide-in-from-bottom-4 duration-300'
      )}
    >
      <button
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
        onClick={handleDismiss}
        aria-label="Fechar"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 pr-4">
        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Smartphone className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">Instalar Legacy CRM</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {iosMode
              ? 'Adicione à tela inicial para acesso rápido e modo offline.'
              : 'Adicione à tela inicial para acesso rápido, notificações e modo offline.'}
          </p>
        </div>
      </div>

      {iosMode ? (
        <>
          <ol className="mt-3 space-y-1.5 text-xs text-muted-foreground">
            <li className="flex items-center gap-2">
              <span className="h-4 w-4 rounded-full bg-muted text-foreground text-[10px] font-bold flex items-center justify-center shrink-0">1</span>
              <span className="flex-1">Toque em <Share className="inline h-3 w-3 mx-0.5 -mt-0.5" /> <strong className="text-foreground">Compartilhar</strong></span>
            </li>
            <li className="flex items-center gap-2">
              <span className="h-4 w-4 rounded-full bg-muted text-foreground text-[10px] font-bold flex items-center justify-center shrink-0">2</span>
              <span className="flex-1">Selecione <Plus className="inline h-3 w-3 mx-0.5 -mt-0.5" /> <strong className="text-foreground">Adicionar à Tela de Início</strong></span>
            </li>
          </ol>
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={handleDismiss}>
              Entendi
            </Button>
          </div>
        </>
      ) : (
        <div className="flex gap-2 mt-3">
          <Button size="sm" className="flex-1 gap-1.5 h-8 text-xs" onClick={handleInstall}>
            <Download className="h-3.5 w-3.5" /> Instalar
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs px-3" onClick={handleDismiss}>
            Agora não
          </Button>
        </div>
      )}
    </div>
  );
}

export default PWAInstallPrompt;
